import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import './ManageTrendLineSignalLog.css';
import { buildApiUrl, buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

Modal.setAppElement('#root');

export default function ManageTrendLineSignalLog() {
  const { workspace, exchange, marketType } = useWorkspace();
  // ─── State & dropdown options ─────────────────────────────────────────
  const [dataList, setDataList] = useState([]);
  const [coinDropdownOptions, setCoinDropdownOptions] = useState([]);
  const trendLineTypeOptions = [
    'HTF_DOWNWARD_TL','HTF_UPWARD_TL',
    'LTF_DOWNWARD_TL','LTF_UPWARD_TL'
  ];
  const [timeFrameOptions] = useState(['5','15','30','60','240', '480', '720', '1440']);
  const trendLineStatusOptions = ['ACTIVE','ADJUST','ADJUSTED','EXPIRED'];
  const trendLineBreakOutStatusOptions = [
    'TL_CONFIRMED_BULL_CROSS',
    'TL_CONFIRMED_BEAR_CROSS',
    'TL_FIRST_CANDLE_BULL_CROSS',
    'TL_FIRST_CANDLE_BEAR_CROSS',
    'TL_FIRST_CANDLE_BULL_CROSS_LEVEL_WATCH',
    'TL_FIRST_CANDLE_BEAR_CROSS_LEVEL_WATCH'
  ];

  const initialFilters = {
    coin: '',
    timeFrame: '',
    candleCloseTime: '',
    trendLineType: '',
    trendLineName: '',
    trendLineStartingPoint: '',
    trendLineStatus: '',              // ← added
    trendLineBreakOutStatus: '',      // ← added
    activeFlag: '',
    priceXTLConfirmedBullCrossAchieved: false,
    priceXTLConfirmedBearCrossAchieved: false,
    strictFlag: false,
    comments: ''
  };

  const [filters, setFilters] = useState(initialFilters);
  const [filteredList, setFilteredList] = useState([]);
  const [newRow, setNewRow] = useState({
    coin: '', timeFrame: '', candleCloseTime: '',
    trendLineType: '', trendLineName: '', trendLineStartingPoint: '',
    breakOutPriceLevel: '', trendLineStatus: '',
    trendLineBreakOutStatus: '', recentSwingHighLow: '',
    priceXTLConfirmedBullCrossAchieved: false,
    priceXTLConfirmedBearCrossAchieved: false,
    strictFlag: false, activeFlag: 'Y'
  });

  const [editIndex, setEditIndex]     = useState(null);
  const [editRowData, setEditRowData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ show:false, id:null });

  // ─── Unique coins for coin‐filter datalist ─────────────────────────────
  const uniqueCoins = useMemo(() => {
    const coins = dataList.map(d => d.coin).filter(Boolean);
    return Array.from(new Set(coins));
  }, [dataList]);

  // ─── Fetch on mount ───────────────────────────────────────────────────
  useEffect(() => {
    fetchLogs();
    fetchCoinDropdown();
  }, [exchange, marketType]);

  async function fetchLogs() {
    try {
      const res = await fetch(buildScopedApiUrl(`/api/bot/ui/get/getAllTrendLineSignalLog`, workspace),
                      {
                        headers: getApiHeaders()
                      });
      const payload = await res.json();
      const normalized = ensureArray(payload);
      if (!Array.isArray(payload)) {
        console.warn('Unexpected trend line log payload shape', payload);
      }
      setDataList(normalized);
    } catch (err) {
      console.error(err);
      setDataList([]);
    }
  }

  async function fetchCoinDropdown() {
    try {
      const res = await fetch(buildScopedApiUrl(`/api/bot/ui/get/getRecentActiveAlertSymbols`, workspace),
                      {
                        headers: getApiHeaders()
                      });
      const payload = await res.json();
      setCoinDropdownOptions(ensureArray(payload));
    } catch (err) {
      console.error('Failed to fetch coin dropdown list:', err);
      setCoinDropdownOptions([]);
    }
  }

  // ─── Apply filters ────────────────────────────────────────────────────
  useEffect(() => {
    setFilteredList(dataList.filter(item =>
      // coin: exact match now
      (!filters.coin            || item.coin === filters.coin) &&
      (!filters.timeFrame       || String(item.timeFrame) === filters.timeFrame) &&
      (!filters.candleCloseTime || item.candleCloseTime.includes(filters.candleCloseTime)) &&
      (!filters.trendLineType   || item.trendLineType === filters.trendLineType) &&
      (!filters.trendLineName   || item.trendLineName.includes(filters.trendLineName)) &&
      (!filters.trendLineStartingPoint || item.trendLineStartingPoint.includes(filters.trendLineStartingPoint)) &&
      (!filters.trendLineStatus         || item.trendLineStatus === filters.trendLineStatus) &&
      (!filters.trendLineBreakOutStatus || item.trendLineBreakOutStatus === filters.trendLineBreakOutStatus) &&
      // your two new boolean filters:
      (!filters.priceXTLConfirmedBullCrossAchieved  || item.priceXTLConfirmedBullCrossAchieved) &&
      (!filters.priceXTLConfirmedBearCrossAchieved  || item.priceXTLConfirmedBearCrossAchieved) &&
      (!filters.strictFlag                          || item.strictFlag) &&
      (!filters.activeFlag       || item.activeFlag === filters.activeFlag) && 
      (!filters.comments       || item.comments === filters.comments)
    ));
  }, [dataList, filters]);
  

  const handleFilterChange = (e, field) => {
    setFilters(f => ({ ...f, [field]: e.target.value }));
  };

  // ─── Clear all filters ────────────────────────────────────────────────
  function handleClearFilters() {
    setFilters(initialFilters);
  }

  // ─── Sorting ──────────────────────────────────────────────────────────
  const [sortConfig, setSortConfig] = useState({ key:null, direction:'asc' });
  function requestSort(key) {
    setSortConfig(s => ({
      key,
      direction: s.key===key && s.direction==='asc' ? 'desc' : 'asc'
    }));
  }
  const sortedList = useMemo(() => {
    const arr = [...filteredList];
    if (sortConfig.key) {
      arr.sort((a,b) => {
        const A = (a[sortConfig.key] ?? '').toString();
        const B = (b[sortConfig.key] ?? '').toString();
        if (A < B) return sortConfig.direction==='asc' ? -1 : 1;
        if (A > B) return sortConfig.direction==='asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [filteredList, sortConfig]);

  // ─── CRUD Handlers ───────────────────────────────────────────────────
  async function handleAdd() {
    if (!newRow.coin || !newRow.timeFrame || !newRow.trendLineType || !newRow.trendLineName || !newRow.trendLineStartingPoint) {
      return alert('Coin, Time Frame, Trend Line Type & Name & startingPoint are required');
    }
    await fetch(buildScopedApiUrl(`/api/bot/ui/get/saveTrendLineSignalLog`, workspace), {
      method:'POST', headers:getApiHeaders({'Content-Type':'application/json'}),
      body:JSON.stringify({ ...newRow, exchange, marketType })
    });
    setNewRow({
      coin:'', timeFrame:'', candleCloseTime:'',
      trendLineType:'', trendLineName:'', trendLineStartingPoint: '',
      breakOutPriceLevel:'', trendLineStatus:'',
      trendLineBreakOutStatus:'', recentSwingHighLow:'',
      priceXTLConfirmedBullCrossAchieved:false,
      priceXTLConfirmedBearCrossAchieved:false,
      strictFlag:false, activeFlag:'Y', comments: ''
    });
    fetchLogs();
  }


function handleEdit(idx) {
  const row = sortedList[idx];
  setEditIndex(idx);
  setEditRowData({
    ...row,
    // if row.activeFlag is null/undefined, default to 'Y'
    activeFlag: row.activeFlag ?? 'Y'
  });
}


  async function handleSave() {
    const original = sortedList[editIndex];
    // make a copy of what we'll send
    //let payload = { ...editRowData };

       let payload = {
         ...editRowData,
         activeFlag: editRowData.activeFlag || 'Y'
       };
  
    // if we’re changing from ADJUST → ACTIVE, clear the extra fields:
    if (
      original.trendLineStatus === 'ADJUST' &&
      editRowData.trendLineStatus   === 'ACTIVE'
    ) {
      payload.breakOutPriceLevel               = '';
      payload.recentSwingHighLow               = '';
      payload.priceXTLConfirmedBullCrossAchieved = false;
      payload.priceXTLConfirmedBearCrossAchieved = false;
    }
  
    await fetch(buildScopedApiUrl(`/api/bot/ui/get/saveTrendLineSignalLog`, workspace), {
      method:  'POST',
      headers: getApiHeaders({'Content-Type':'application/json'}),
      body:    JSON.stringify({ ...payload, exchange, marketType })
    });
  
    setEditIndex(null);
    fetchLogs();
  }
  

  async function handleDelete(id) {
    await fetch(buildApiUrl(`/api/bot/ui/get/deleteTrendLineSignalLogById`, { id }), {
      method:'DELETE',    
        headers: getApiHeaders()
    });
    setConfirmDelete({ show:false, id:null });
    fetchLogs();
  }

  async function handleBulkDelete() {
    if (!filteredList.length) return;
    if (!window.confirm(`Delete ${filteredList.length} filtered entries?`)) return;
    const ids = filteredList.map(i => i.id);
    await fetch(buildScopedApiUrl(`/api/bot/ui/get/deleteTrendLineSignalLogByIds`, workspace),{
      method:'DELETE',
       headers:getApiHeaders({'Content-Type':'application/json'}),
      body:JSON.stringify(ids)
    });
    fetchLogs();
  }

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="trend-line-log-page">
                <h2 className="page-title">Manage Trend Line Signal Logs</h2>
      <div className="workspace-subtitle">Workspace: {exchange} / {marketType}</div>
      <div className="header-actions">
        <button className="btn-delete-all" onClick={handleBulkDelete}>
          Delete All Filtered
        </button>
        <button className="btn-clear-filters" onClick={handleClearFilters}>
          Clear Filters
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              {[
                'coin','timeFrame','candleCloseTime','trendLineType','trendLineName','trendLineStartingPoint',
                'breakOutPriceLevel','trendLineStatus','trendLineBreakOutStatus',
                'recentSwingHighLow',
                'priceXTLConfirmedBullCrossAchieved','priceXTLConfirmedBearCrossAchieved',
                'strictFlag','activeFlag','comments'
              ].map(col => (
                <th key={col} onClick={()=>requestSort(col)}>
                  {col}{sortConfig.key===col?(sortConfig.direction==='asc'?' ↑':' ↓'):''}
                </th>
              ))}
              <th>Actions</th>
            </tr>
            <tr className="filter-row">
              {/* coin filter: typing + dropdown */}
              <td>
                <input
                  list="coinList"
                  placeholder="Coin"
                  value={filters.coin}
                  onChange={e=>handleFilterChange(e,'coin')}
                />
              </td>
              {/* timeFrame filter: select */}
              <td>
                <select
                  value={filters.timeFrame}
                  onChange={e=>handleFilterChange(e,'timeFrame')}
                >
                  <option value="">All TF</option>
                  {timeFrameOptions.map(tf=>(
                    <option key={tf} value={tf}>{tf}</option>
                  ))}
                </select>
              </td>
              {/* closeTime */}
              <td>
                <input
                  placeholder="CloseTime"
                  value={filters.candleCloseTime}
                  onChange={e=>handleFilterChange(e,'candleCloseTime')}
                />
              </td>
              {/* trendLineType */}
              <td>
                <select
                  value={filters.trendLineType}
                  onChange={e=>handleFilterChange(e,'trendLineType')}
                >
                  <option value="">All Types</option>
                  {trendLineTypeOptions.map(o=>(
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </td>
              {/* trendLineName */}
              <td>
                <input
                  placeholder="Name"
                  value={filters.trendLineName}
                  onChange={e=>handleFilterChange(e,'trendLineName')}
                />
              </td>
              <td>
                <input
                  placeholder="CandleCloseTime"
                  value={filters.trendLineStartingPoint}
                  onChange={e=>handleFilterChange(e,'trendLineStartingPoint')}
                />
              </td>
              {/* breakOutPriceLevel */}
              <td>
                <input
                  placeholder="BreakOut Price"
                  value={filters.breakOutPriceLevel||''}
                  onChange={e=>handleFilterChange(e,'breakOutPriceLevel')}
                />
              </td>
              {/* trendLineStatus */}
              <td>
                <select
                  value={filters.trendLineStatus}
                  onChange={e=>handleFilterChange(e,'trendLineStatus')}
                >
                  <option value="">All Status</option>
                  {trendLineStatusOptions.map(s=>(
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              {/* trendLineBreakOutStatus */}
              <td>
                <select
                  value={filters.trendLineBreakOutStatus}
                  onChange={e=>handleFilterChange(e,'trendLineBreakOutStatus')}                >
                  <option value="">All BreakOut</option>
                  {trendLineBreakOutStatusOptions.map(s=>(
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              {/* recentSwingHighLow */}
              <td>
                <input
                  placeholder="Swing HL"
                  value={filters.recentSwingHighLow||''}
                  onChange={e=>handleFilterChange(e,'recentSwingHighLow')}
                />
              </td>
              {/* priceXTLConfirmedBullCrossAchieved */}
              <td>
                <input
                  type="checkbox"
                  checked={filters.priceXTLConfirmedBullCrossAchieved||false}
                  onChange={e=>setFilters(f=>({...f,priceXTLConfirmedBullCrossAchieved:e.target.checked}))}
                />
              </td>
              {/* priceXTLConfirmedBearCrossAchieved */}
              <td>
                <input
                  type="checkbox"
                  checked={filters.priceXTLConfirmedBearCrossAchieved||false}
                  onChange={e=>setFilters(f=>({...f,priceXTLConfirmedBearCrossAchieved:e.target.checked}))}
                />
              </td>
              {/* strictFlag */}
              <td>
                <input
                  type="checkbox"
                  checked={filters.strictFlag||false}
                  onChange={e=>setFilters(f=>({...f,strictFlag:e.target.checked}))}
                />
              </td>
              {/* activeFlag */}
              <td>
                <select
                  value={filters.activeFlag}
                  onChange={e=>handleFilterChange(e,'activeFlag')}
                >
                  <option value="">All</option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </td>
              <td>
                <input
                  placeholder="comments"
                  value={filters.comments}
                  onChange={e=>handleFilterChange(e,'comments')}
                />
              </td>
              <td></td>
            </tr>
          </thead>
          <tbody>
            {sortedList.map((row, idx) => (
              <tr key={row.id}>
                {[
                  'coin','timeFrame','candleCloseTime','trendLineType','trendLineName','trendLineStartingPoint',
                  'breakOutPriceLevel','trendLineStatus','trendLineBreakOutStatus',
                  'recentSwingHighLow',
                  'priceXTLConfirmedBullCrossAchieved','priceXTLConfirmedBearCrossAchieved',
                  'strictFlag','activeFlag','comments'
                ].map(field => (
                  <td
                  key={field}
                  style={
                    field==='trendLineStatus'
                    && editIndex!==idx
                    && row.trendLineStatus==='ADJUST'
                      ? { color: 'red' }
                      : undefined
                  }
                >
                    {editIndex===idx ? (
                    // checkboxes stay the same
                    (field.endsWith('Achieved') || field==='strictFlag') ? (
                      <input
                        type="checkbox"
                        checked={editRowData[field]}
                        onChange={e => setEditRowData(r => ({ ...r, [field]: e.target.checked }))}
                      />
                    ) :

                    // trendLineStatus dropdown
                    field === 'trendLineStatus' ? (
                      <select
                        value={editRowData.trendLineStatus}
                        onChange={e => setEditRowData(r => ({ ...r, trendLineStatus: e.target.value }))}
                      >
                        <option value="">Select Status</option>
                        {trendLineStatusOptions.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) :

                    // trendLineBreakOutStatus dropdown
                    field === 'trendLineBreakOutStatus' ? (
                      <select
                        value={editRowData.trendLineBreakOutStatus}
                        onChange={e => setEditRowData(r => ({ ...r, trendLineBreakOutStatus: e.target.value }))}
                      >
                        <option value="">Select BreakOut</option>
                        {trendLineBreakOutStatusOptions.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) :

                    // timeFrame dropdown
                    field === 'timeFrame' ? (
                      <select
                        value={editRowData.timeFrame}
                        onChange={e => setEditRowData(r => ({ ...r, timeFrame: e.target.value }))}
                      >
                        <option value="">Select TF</option>
                        {timeFrameOptions.map(tf => (
                          <option key={tf} value={tf}>{tf}</option>
                        ))}
                      </select>
                    ) :

                    // activeFlag stays a dropdown
                    field === 'activeFlag' ? (
                      <select
                        value={editRowData.activeFlag}
                        onChange={e => setEditRowData(r => ({ ...r, activeFlag: e.target.value }))}
                      >
                        <option value="Y">Y</option>
                        <option value="N">N</option>
                      </select>
                    ) :

                    // everything else remains a text input
                    <input
                      value={editRowData[field] || ''}
                      onChange={e => setEditRowData(r => ({ ...r, [field]: e.target.value }))}
                    />
                  ) : (
                    row[field] == null 
                    ? '' 
                    : String(row[field])
                  )}

                  </td>
                ))}
                <td className="actions-cell">
                  {editIndex===idx ? (
                    <>
                      <button className="btn-save"   onClick={handleSave}>Save</button>
                      <button className="btn-cancel" onClick={()=>setEditIndex(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="btn-edit"   onClick={()=>handleEdit(idx)}>Edit</button>
                      <button className="btn-delete" onClick={()=>setConfirmDelete({show:true,id:row.id})}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}

            {/* New Row */}
            <tr>
              <td>
                <select
                  value={newRow.coin}
                  onChange={e=>setNewRow(n=>({...n,coin:e.target.value}))}
                >
                  <option value="">Select Coin</option>
                  {coinDropdownOptions.map(c=>(
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={newRow.timeFrame}
                  onChange={e=>setNewRow(n=>({...n,timeFrame:e.target.value}))}
                >
                  <option value="">Select TF</option>
                  {timeFrameOptions.map(tf=>(
                    <option key={tf} value={tf}>{tf}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="text"
                  placeholder="CloseTime"
                  value={newRow.candleCloseTime}
                  onChange={e=>setNewRow(n=>({...n,candleCloseTime:e.target.value}))}
                />
              </td>
              <td>
                <select
                  value={newRow.trendLineType}
                  onChange={e=>setNewRow(n=>({...n,trendLineType:e.target.value}))}
                >
                  <option value="">Select Type</option>
                  {trendLineTypeOptions.map(o=>(
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Name"
                  value={newRow.trendLineName}
                  onChange={e=>setNewRow(n=>({...n,trendLineName:e.target.value}))}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="CandleCloseTime"
                  value={newRow.trendLineStartingPoint}
                  onChange={e=>setNewRow(n=>({...n,trendLineStartingPoint:e.target.value}))}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="BreakOut Price"
                  value={newRow.breakOutPriceLevel}
                  onChange={e=>setNewRow(n=>({...n,breakOutPriceLevel:e.target.value}))}
                />
              </td>
              <td>
                <select
                  value={newRow.trendLineStatus}
                  onChange={e=>setNewRow(n=>({...n,trendLineStatus:e.target.value}))}
                >
                  <option value="">Status</option>
                  {trendLineStatusOptions.map(s=>(
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={newRow.trendLineBreakOutStatus}
                  onChange={e=>setNewRow(n=>({...n,trendLineBreakOutStatus:e.target.value}))}
                >
                  <option value="">BreakOut</option>
                  {trendLineBreakOutStatusOptions.map(s=>(
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Swing HL"
                  value={newRow.recentSwingHighLow}
                  onChange={e=>setNewRow(n=>({...n,recentSwingHighLow:e.target.value}))}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={newRow.priceXTLConfirmedBullCrossAchieved}
                  onChange={e=>setNewRow(n=>({...n,priceXTLConfirmedBullCrossAchieved:e.target.checked}))}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={newRow.priceXTLConfirmedBearCrossAchieved}
                  onChange={e=>setNewRow(n=>({...n,priceXTLConfirmedBearCrossAchieved:e.target.checked}))}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={newRow.strictFlag}
                  onChange={e=>setNewRow(n=>({...n,strictFlag:e.target.checked}))}
                />
              </td>
              <td>
                <select
                  value={newRow.activeFlag}
                  onChange={e=>setNewRow(n=>({...n,activeFlag:e.target.value}))}
                >
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </td>
              <td>
                <input
                  type="text"
                  placeholder="comments"
                  value={newRow.comments}
                  onChange={e=>setNewRow(n=>({...n,comments:e.target.value}))}
                />
              </td>
              <td className="actions-cell">
                <button className="btn-add" onClick={handleAdd}>Add</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <datalist id="coinList">
        {uniqueCoins.map(c => <option key={c} value={c} />)}
      </datalist>

      {confirmDelete.show && (
        <Modal
          isOpen
          onRequestClose={()=>setConfirmDelete({show:false,id:null})}
          className="confirm-modal"
          overlayClassName="confirm-overlay"
        >
          <h3>Confirm Delete?</h3>
          <button className="btn-delete" onClick={()=>handleDelete(confirmDelete.id)}>Yes</button>
          <button className="btn-cancel" onClick={()=>setConfirmDelete({show:false,id:null})}>No</button>
        </Modal>
      )}
    </div>
  );
}
